import { useRegion } from '@/context/RegionContext';
import { useSiteSettings } from '@/context/SiteSettingsContext';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { useEffect } from 'react';

const REGION_CONFIG = {
  us: { flag: 'https://flagcdn.com/w20/us.png', label: 'US', fullName: 'Amazon US' },
  uk: { flag: 'https://flagcdn.com/w20/gb.png', label: 'UK', fullName: 'Amazon UK' },
  es: { flag: 'https://flagcdn.com/w20/es.png', label: 'ES', fullName: 'Amazon ES' },
};

const RegionSelector = () => {
  const { region, setRegionTo } = useRegion();
  const { availableRegions } = useSiteSettings();

  // If current region is not in available regions, switch to first available
  useEffect(() => {
    if (!availableRegions.includes(region)) {
      setRegionTo(availableRegions[0]);
    }
  }, [availableRegions, region, setRegionTo]);

  // If only one region is available, show static display
  if (availableRegions.length === 1) {
    const singleRegion = availableRegions[0];
    const config = REGION_CONFIG[singleRegion];
    return (
      <div
        className="h-9 inline-flex items-center justify-center gap-2 px-3 border border-site-text font-mono text-xs uppercase tracking-wide"
        data-testid="region-display"
      >
        <img 
          src={config.flag} 
          alt={config.label}
          className="w-5 h-3"
        />
        <span className="translate-y-[2px]">Amazon {config.label}</span>
      </div>
    );
  }

  // Show dropdown for multiple regions
  const currentConfig = REGION_CONFIG[region] || REGION_CONFIG.us;

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <button
          className="h-9 inline-flex items-center justify-center gap-2 px-3 border border-site-text hover:bg-site-bg-dark hover:text-white transition-colors font-mono text-xs uppercase tracking-wide"
          data-testid="region-selector-btn"
        >
          <img 
            src={currentConfig.flag} 
            alt={currentConfig.label}
            className="w-5 h-3"
          />
          <span className="translate-y-[2px]">Amazon {currentConfig.label}</span>
        </button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="bg-white border border-site-text">
        {availableRegions.map((regionKey) => {
          const config = REGION_CONFIG[regionKey];
          return (
            <DropdownMenuItem
              key={regionKey}
              onClick={() => setRegionTo(regionKey)}
              className={`font-mono text-xs uppercase tracking-wide cursor-pointer ${
                region === regionKey ? 'bg-site-bg-alt' : ''
              }`}
              data-testid={`region-${regionKey}-option`}
            >
              <img src={config.flag} alt={config.label} className="w-5 h-3 object-cover mr-2" />
              {config.fullName}
            </DropdownMenuItem>
          );
        })}
      </DropdownMenuContent>
    </DropdownMenu>
  );
};

export default RegionSelector;
